import { useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';
import { needsRemoteUpload } from './bodyImages';

interface Options {
  editor: Editor | null;
  uploadImage?: (file: File) => Promise<string>;
  onError?: (err: unknown) => void;
}

// srcs currently being fetched/uploaded by a scan, so concurrent updates don't
// process the same image twice. Module-level: one editor instance per page.
const processing = new Set<string>();

// Position of the first image node whose src matches, so an async upload can swap
// that specific node's src (or remove it on failure) without disturbing others.
function findImagePos(editor: Editor, src: string): number | null {
  let found: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (found !== null) return false;
    if (node.type.name === 'image' && node.attrs.src === src) found = pos;
    return true;
  });
  return found;
}

function setSrcAt(editor: Editor, pos: number, src: string): void {
  editor.commands.command(({ tr }) => {
    tr.setNodeAttribute(pos, 'src', src);
    return true;
  });
}

function deleteAt(editor: Editor, pos: number): void {
  editor.commands.command(({ tr }) => {
    const node = tr.doc.nodeAt(pos);
    if (node) tr.delete(pos, pos + node.nodeSize);
    return true;
  });
}

function toFile(blob: Blob): File {
  return blob instanceof File ? blob : new File([blob], 'image', { type: blob.type || 'image/png' });
}

export function useBodyImageUploads({ editor, uploadImage, onError }: Options) {
  // Insert an optimistic blob: preview, upload it, then swap to the stored S3
  // URL. On failure remove the node and report the error. Save stays blocked
  // while the blob: src is in the document (PostEditor's bodyHasPendingUpload).
  const insertBlob = useCallback(
    async (blob: Blob): Promise<void> => {
      if (!editor || !uploadImage) return;
      const localUrl = URL.createObjectURL(blob);
      editor.chain().focus().setImage({ src: localUrl }).run();
      try {
        const s3 = await uploadImage(toFile(blob));
        const pos = findImagePos(editor, localUrl);
        if (pos !== null) setSrcAt(editor, pos, s3);
      } catch (err) {
        const pos = findImagePos(editor, localUrl);
        if (pos !== null) deleteAt(editor, pos);
        onError?.(err);
      } finally {
        URL.revokeObjectURL(localUrl);
      }
    },
    [editor, uploadImage, onError],
  );

  // Upload-and-insert any image files on the clipboard (screenshots, Word, a
  // single Docs image). Returns true to take over the paste. Non-image pastes
  // return false so normal text/HTML paste runs — googleusercontent/data: images
  // in that HTML are handled afterward by scanRemoteImages (Task 5).
  const handlePaste = useCallback(
    (_view: EditorView, event: ClipboardEvent): boolean => {
      if (!uploadImage) return false;
      const items = event.clipboardData?.items;
      if (!items) return false;
      const files = Array.from(items)
        .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length === 0) return false;
      event.preventDefault();
      files.forEach((f) => void insertBlob(f));
      return true;
    },
    [uploadImage, insertBlob],
  );

  // Find images whose src is a Google Docs hotlink or a data: URI, fetch the
  // bytes, show an optimistic blob: preview, upload to S3, and swap the src.
  // S3 and blob: srcs never match needsRemoteUpload, so this can't loop or touch
  // already-stored images. A fetch failure (e.g. CORS) leaves the remote URL as
  // a hotlink fallback rather than dropping the image.
  const scanRemoteImages = useCallback(() => {
    if (!editor || !uploadImage) return;
    const targets: string[] = [];
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'image') {
        const src = node.attrs.src as string;
        if (needsRemoteUpload(src) && !processing.has(src)) targets.push(src);
      }
      return true;
    });
    for (const remoteSrc of targets) {
      processing.add(remoteSrc);
      void (async () => {
        let localUrl: string | null = null;
        try {
          const resp = await fetch(remoteSrc);
          if (!resp.ok) throw new Error(`fetch ${resp.status}`);
          const blob = await resp.blob();
          localUrl = URL.createObjectURL(blob);
          const pos = findImagePos(editor, remoteSrc);
          if (pos !== null) setSrcAt(editor, pos, localUrl);
          const s3 = await uploadImage(toFile(blob));
          const pos2 = findImagePos(editor, localUrl);
          if (pos2 !== null) setSrcAt(editor, pos2, s3);
        } catch (err) {
          // Leave the remote URL in place as a best-effort hotlink fallback.
          onError?.(err);
        } finally {
          if (localUrl) URL.revokeObjectURL(localUrl);
          processing.delete(remoteSrc);
        }
      })();
    }
  }, [editor, uploadImage, onError]);

  return { insertBlob, handlePaste, scanRemoteImages };
}
